import axios from "axios";
import { useInstanceSettingsStore } from "#src/stores/instance-settings.ts";
import { type Pinia } from "pinia";
import { useEappStore } from "#src/stores/electronic-application.ts";

export const httpClient = (pinia: Pinia) => {
  const instance = useInstanceSettingsStore(pinia);
  const $httpClient = axios.create({
    baseURL: `${instance.api_url}/strife/v1`,
  });

  $httpClient.interceptors.response.use(
    response => {
      // uncomment to debug responses
      // console.log(response.data);
      return response;
    },
    error => {
      // Fairway returned an error
      if (!error.status && error.message === "Network Error") {
        throw "Please check your internet connection and try again.";
      }
      throw error;
    }
  );

  $httpClient.interceptors.request.use(config => {
    // uncomment to debug requests
    // console.log(config.data);
    const instance = useInstanceSettingsStore(pinia);
    if (instance.prefilledNpn) {
      config.headers["X-STRIFE-ID"] = instance.prefilledNpn;
    }

    if (instance.domain_key) {
      config.headers["X-STRIFE-KEY"] = instance.domain_key;
    }

    if (instance.api_key) {
      config.headers["X-BACKNINE-AUTHENTICATION"] = instance.api_key;
    }

    if (instance.activity_id) {
      config.headers["X-QNA-ACTIVITY-ID"] = instance.activity_id;
    }

    return config;
  });

  return $httpClient;
};

export const attachEappData = (pinia: Pinia, params: any) => {
  const eapp = useEappStore(pinia);
  return {
    electronic_application_id: eapp.id,
    uuid: eapp.uuid,
    ...params,
  };
};

interface BaseRequestArgs {
  baseUrl?: string;
  url: string;
  includeEappData?: boolean;
}

interface PutRequestArgs extends BaseRequestArgs {
  body?: any;
}

interface GetRequestArgs extends BaseRequestArgs {
  params?: any;
}

function httpMethod(
  pinia: Pinia,
  args: PutRequestArgs,
  method: "get" | "delete" | "put" | "post"
) {
  const instance = useInstanceSettingsStore(pinia);
  const fullUrl = (args.baseUrl || instance.api_url) + args.url;

  return httpClient(pinia)[method](fullUrl, args.body);
}

export const Put = (pinia: Pinia, args: PutRequestArgs) => {
  let fullBody = args.body;
  if (args.includeEappData) fullBody = attachEappData(pinia, fullBody || {});
  return httpMethod(pinia, { ...args, body: fullBody }, "put");
};

export const Post = (pinia: Pinia, args: PutRequestArgs) => {
  let fullBody = args.body;
  if (args.includeEappData) fullBody = attachEappData(pinia, fullBody || {});
  return httpMethod(pinia, { ...args, body: fullBody }, "post");
};

export const Get = (pinia: Pinia, args: GetRequestArgs) => {
  let fullBody = args.params;
  if (args.includeEappData) fullBody = attachEappData(pinia, fullBody || {});
  return httpMethod(pinia, { ...args, body: { params: fullBody } }, "get");
};

export const Delete = (pinia: Pinia, args: GetRequestArgs) => {
  let fullBody = args.params;
  if (args.includeEappData) fullBody = attachEappData(pinia, fullBody || {});
  return httpMethod(pinia, { ...args, body: { params: fullBody } }, "delete");
};
